import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SUPPORTED_LANGUAGES } from './LanguageLayout';
import { getDomainForLang, SUPPORTED_LANGUAGES as SEO_LANGUAGES } from '@/lib/seoConfig';

interface LanguageRedirectProps {
  to?: string;
}

// Get default language based on domain - called inside component to avoid SSR issues
const getDefaultLanguageForDomain = (): string => {
  if (typeof window === 'undefined') {
    return 'en'; // Default for SSR/prerendering
  }
  const hostname = window.location.hostname;
  // .se domain defaults to Swedish, .com and others default to English
  if (hostname.endsWith('.se') || hostname === 'estylingstudio.se') {
    return 'sv';
  }
  return 'en';
};

export const LanguageRedirect = ({ to }: LanguageRedirectProps) => {
  const location = useLocation();
  const params = useParams();
  
  // Get language from various sources
  const urlParams = new URLSearchParams(location.search);
  const queryLang = urlParams.get('lang');
  
  // Get domain-specific default - this is the PRIMARY source of truth
  const domainDefault = getDefaultLanguageForDomain();
  
  // Priority: query param > domain default (localStorage ignored for initial redirect)
  let targetLang = domainDefault;
  
  if (queryLang && SUPPORTED_LANGUAGES.includes(queryLang)) {
    targetLang = queryLang;
  }

  // Build target path
  let targetPath = to || '';
  
  // Handle project routes with params
  if (to === '/project' && params.projectId) {
    targetPath = `/project/${params.projectId}`;
    if (params['*']) {
      targetPath += `/${params['*']}`;
    }
  }
  
  // Remove query param from search string
  urlParams.delete('lang');
  const newSearch = urlParams.toString();
  
  const finalPath = `/${targetLang}${targetPath}${newSearch ? `?${newSearch}` : ''}`;
  
  // Build canonical URL for the redirect target - always use non-www domain from seoConfig
  const domain = getDomainForLang(targetLang);
  const canonicalUrl = `${domain}/${targetLang}/`;

  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <Navigate to={finalPath} replace />
    </>
  );
};
